import type { MutableRefObject } from 'react';
import { HubConnection } from '@microsoft/signalr';
import { AolAddedDTO, AolSectionSignalRDTO, AolSectionSignalRUpdate } from '../types/Dto/AolSectionSignalRDTO';
import { AolSectionDataUpdateDto } from '../types/Dto/AolSectionDataUpdateDto';
import { TankersEventDataBM } from '../types/BusinessModel/TankersEventDataBM';
import { ValidatedNoonReportFactory } from '../factory/ValidatedNoonReportFactory';
import { ValidatedNoonReportBM } from '../types/BusinessModel/ValidatedNoonReportBM';
import { ImosPortCallSyncDto } from '../types/Dto/ImosPortCallSyncDto';
import { SignalRResponse } from '../types/Dto/SignalRResponse';
import { SignalRMessageType } from '../types/Dto/SignalRMessageType';
import { GetSectionBM } from '../utils/aolSectionHelper';
import { getStringValue } from '../utils/arrayUtils';
import { formatAolUrlDate } from '../utils/aol/aolDateUtils';
import { AppFeatureEnum } from '../enums/appFeatureEnum';
import { CargoTemperatureLogDate } from '../types/ViewModel/CargoTempLog/CargoTemperatureLogDate';
import { BunkeringBM } from 'src/types/BusinessModel/Bunkering/BunkeringBM';
import { BunkeringStatusBM } from 'src/types/BusinessModel/Bunkering/BunkeringStatusBM';
import { FuelRobBM } from 'src/types/BusinessModel/Bunkering/FuelRobBM';
import { getAllFuelRobItems } from 'src/api/bunkering/offlineBunkeringApi';
import { StowagePlanBM } from 'src/types/BusinessModel/Stowage/StowagePlanBM';
import { StowagePlanTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanTypeEnum';
import { buildQueryParams } from '../api/endpoints/queryParamsHelper';
import { requestThrottleManager } from 'src/utils/requestThrottleManager';
import { logSignalRDiagnostic } from './signalRTelemetry';
import { useAolUpdateHandler } from './useAolUpdateHandler';
import { useBunkerUpdateHandler } from './useBunkerUpdateHandler';
import { useToast } from './useToast';
import { useSWService } from '../api/sw/useSWService';
import useApiEndpoints from '../context/useApiEndpoints';
import useGetAolData from '../api/aol/useGetAolData';
import useGetMasterPlanApi from 'src/api/stowage/useGetMasterPlanApi';
import { useAolStore } from '../context/AolContext';
import { useCargoTempLogStore } from '../context/CargoTempLogContext';
import useCargoTemperatureApi from '../api/cargo-temp-log/useCargoTempLogApi';
import useUpdateMissingTasksIcon from './useUpdateMissingTasksIcon';
import useAppFeature from './useAppFeature';
import { useRotationStore } from 'src/context/RotationContext';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import { useStowagePlanResponseHandler } from 'src/api/responseHandlers/useStowagePlanResponseHandler';
import { makeAolDataItemList, makeAolGroupList } from '../factory/Aol/AolDataItemFactory';
import type { Vessel } from '../types/ViewModel/Vessel/Vessel';
import type { EventApplicationScope } from './useSignalRConnection';

type ApiGetProps = {
    endpoint?: string | null;
    queryParam?: string | null;
    forceRefreshCache?: boolean | null;
};

export interface SignalRMessageHandlersOptions {
    // Connection/session state — plain values, recomputed fresh every render, exactly as this
    // code consumed them before extraction (matching useSignalRConnection.ts's own pattern of
    // never memoizing these message handlers).
    selectedVessel: Vessel | null;
    loggedInUser: unknown;
    hubConnection: HubConnection | null | undefined;
    locationPathname: string;
    aolFilter: Pick<ReturnType<typeof useAolStore>, 'aolFilter'>['aolFilter'];
    cargoTempLogDates: ReturnType<typeof useCargoTempLogStore>['cargoTempLogDates'];
    activeStowagePlan: ReturnType<typeof useStowagePlanStore>['activeStowagePlan'];
    productList: ReturnType<typeof useRotationStore>['productList'];
    portNameList: ReturnType<typeof useRotationStore>['portNameList'];
    berthNameList: ReturnType<typeof useRotationStore>['berthNameList'];
    stowagePlanRotationPortCallList: ReturnType<typeof useRotationStore>['stowagePlanRotationPortCallList'];
    stowagePlanRotationCargoList: ReturnType<typeof useRotationStore>['stowagePlanRotationCargoList'];

    // Shared refs (SAME mutable objects as useSignalRConnection.ts — never replace with copies).
    reconciliationTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;

    // Event application — shared with recovery (useConnectionRecoveryOrchestrator) and the
    // connection-lifecycle generation guards in useSignalRConnection.ts.
    isCurrentEventApplicationScope: (scope: EventApplicationScope | undefined, dto?: TankersEventDataBM) => scope is EventApplicationScope;
    serializeEventApplication: (scope: EventApplicationScope, apply: () => Promise<void>) => Promise<void>;
    applyEventChanges: (dto: TankersEventDataBM, scope: EventApplicationScope, appliedSignalREpoch?: number) => Promise<void>;

    // Update handlers
    updateAolList: ReturnType<typeof useAolUpdateHandler>['updateAolList'];
    addNoonReport: ReturnType<typeof useAolUpdateHandler>['addNoonReport'];
    updateOnDelete: ReturnType<typeof useAolUpdateHandler>['updateOnDelete'];
    updateAolStatusToOtherUsers: ReturnType<typeof useAolUpdateHandler>['updateAolStatusToOtherUsers'];
    toggleToast: ReturnType<typeof useAolUpdateHandler>['toggleToast'];
    updateOnHoursSync: ReturnType<typeof useAolUpdateHandler>['updateOnHoursSync'];
    updateOnImosPortCallIdSync: ReturnType<typeof useAolUpdateHandler>['updateOnImosPortCallIdSync'];
    reconcileGroupStatuses: ReturnType<typeof useAolUpdateHandler>['reconcileGroupStatuses'];
    handleUpdatedBunkering: ReturnType<typeof useBunkerUpdateHandler>['handleUpdatedBunkering'];
    handleUpdatedBunkeringStatus: ReturnType<typeof useBunkerUpdateHandler>['handleUpdatedBunkeringStatus'];
    handleDeletedBunkeringStatus: ReturnType<typeof useBunkerUpdateHandler>['handleDeletedBunkeringStatus'];
    handleUpdatedRobs: ReturnType<typeof useBunkerUpdateHandler>['handleUpdatedRobs'];
    toggleBunkerToast: ReturnType<typeof useBunkerUpdateHandler>['toggleBunkerToast'];

    // Toast
    toastSuccess: ReturnType<typeof useToast>['toastSuccess'];
    toastError: ReturnType<typeof useToast>['toastError'];

    // API / feature dependencies
    getApiData: (props: ApiGetProps) => Promise<any>; // matches useApi().getApiData's own Promise<any> return type
    apiEndpoints: ReturnType<typeof useApiEndpoints>['apiEndpoints'];
    refreshRequestsContaining: ReturnType<typeof useSWService>['refreshRequestsContaining'];
    removeRequestsContaining: ReturnType<typeof useSWService>['removeRequestsContaining'];
    getNoonReportSection: ReturnType<typeof useGetAolData>['getNoonReportSection'];
    getAolItems: ReturnType<typeof useGetAolData>['getAolItems'];
    getMasterStowagePlanFromApi: ReturnType<typeof useGetMasterPlanApi>['getMasterStowagePlanFromApi'];
    setAolItems: ReturnType<typeof useAolStore>['setAolItems'];
    setHasSyncError: ReturnType<typeof useAolStore>['setHasSyncError'];
    getCargoTemperatureConfigs: ReturnType<typeof useCargoTemperatureApi>['getCargoTemperatureConfigs'];
    getCargoTemperatureInputFields: ReturnType<typeof useCargoTemperatureApi>['getCargoTemperatureInputFields'];
    updateAolMissingTasks: ReturnType<typeof useUpdateMissingTasksIcon>['updateAolMissingTasks'];
    isFeatureEnabled: ReturnType<typeof useAppFeature>['isFeatureEnabled'];
    onStowagePlanUpdated: ReturnType<typeof useStowagePlanResponseHandler>['onStowagePlanUpdated'];
    onStowagePlanAdded: ReturnType<typeof useStowagePlanResponseHandler>['onStowagePlanAdded'];
    updateLoadedMasterStowagePlan: ReturnType<typeof useStowagePlanResponseHandler>['updateLoadedMasterStowagePlan'];
}

export interface SignalRMessageHandlers {
    handleSignalRResponse: (
        responseType: SignalRMessageType,
        signalRResponse: SignalRResponse,
        hubConnection: HubConnection,
        scope?: EventApplicationScope
    ) => Promise<void>;
}

/**
 * The ~18-way SignalR message-type routing, kept separate from connection lifecycle.
 * useSignalRConnection.ts retains connection setup/teardown, the message queue/wiring
 * (processMessageQueue, messageQueueRef, retry/backoff), and calls the single
 * handleSignalRResponse entry point returned here for each dequeued message.
 */
export const useSignalRMessageHandlers = (options: SignalRMessageHandlersOptions): SignalRMessageHandlers => {
    const {
        selectedVessel,
        loggedInUser,
        hubConnection,
        locationPathname,
        aolFilter,
        cargoTempLogDates,
        activeStowagePlan,
        productList,
        portNameList,
        berthNameList,
        stowagePlanRotationPortCallList,
        stowagePlanRotationCargoList,
        reconciliationTimerRef,
        isCurrentEventApplicationScope,
        serializeEventApplication,
        applyEventChanges,
        updateAolList,
        addNoonReport,
        updateOnDelete,
        updateAolStatusToOtherUsers,
        toggleToast,
        updateOnHoursSync,
        updateOnImosPortCallIdSync,
        reconcileGroupStatuses,
        handleUpdatedBunkering,
        handleUpdatedBunkeringStatus,
        handleDeletedBunkeringStatus,
        handleUpdatedRobs,
        toggleBunkerToast,
        toastSuccess,
        toastError,
        getApiData,
        apiEndpoints,
        refreshRequestsContaining,
        removeRequestsContaining,
        getNoonReportSection,
        getAolItems,
        getMasterStowagePlanFromApi,
        setAolItems,
        setHasSyncError,
        getCargoTemperatureConfigs,
        getCargoTemperatureInputFields,
        updateAolMissingTasks,
        isFeatureEnabled,
        onStowagePlanUpdated,
        onStowagePlanAdded,
        updateLoadedMasterStowagePlan,
    } = options;

    const validatedNoonReportFactory = new ValidatedNoonReportFactory();

    const getUpdatedSection = async (sectionSignalRUpdate: AolSectionSignalRUpdate, message: SignalRResponse) => {
        if (!selectedVessel?.imoNumber || !loggedInUser) {
            return;
        }
        const updatedNoonReport = await getNoonReportSection(
            selectedVessel.imoNumber,
            0,
            sectionSignalRUpdate.section,
            true,
            sectionSignalRUpdate.noonReportGuid
        );
        const updatedSection = GetSectionBM(sectionSignalRUpdate.section, updatedNoonReport);

        const dto: AolSectionSignalRDTO = {
            section: updatedSection,
            noonReportId: updatedNoonReport?.metadata?.noonReportId,
            noonReportGuid: sectionSignalRUpdate.noonReportGuid,
            imoNumber: sectionSignalRUpdate.imoNumber,
            indexInDay: 0,
        };
        updateAolList(dto);
        toggleToast(dto.noonReportId ?? 0, message, hubConnection);
    };

    const getDistinctCargoNumbers = (logDates: CargoTemperatureLogDate[]): string[] => {
        const cargoNumbersSet = new Set<string>();

        logDates.forEach((logDate) => {
            logDate.cargoInTankList?.forEach((cargoInTank) => {
                cargoNumbersSet.add(cargoInTank.cargoNumber);
            });
        });

        return Array.from(cargoNumbersSet);
    };

    const getNoonReports = async (imoNumber: number, fromDate: string, toDate: string) => {
        const requestKey = `SignalR:getNoonReportsForShip:${imoNumber}:${fromDate}:${toDate}`;

        return requestThrottleManager.executeBypassCooldown(requestKey, async () => {
            const result = await getApiData({
                endpoint: apiEndpoints['getNoonReportsForShip'].url,
                queryParam: buildQueryParams({ imoNumber, fromDate, toDate }),
                forceRefreshCache: true,
            });

            if (result) {
                return validatedNoonReportFactory.createList(result);
            }
            return [];
        });
    };

    /**
     * Optimized handler for refreshing GetNoonReportsForShip endpoint
     * Uses deduplication to prevent multiple parallel calls
     */
    const throttledRefreshNoonReports = (imoNumber?: number) => {
        if (!imoNumber && selectedVessel) {
            imoNumber = selectedVessel.imoNumber;
        }

        if (!imoNumber) return;

        const requestKey = `refreshNoonReports:${imoNumber}`;

        // Use executeBypassCooldown to ensure SignalR-triggered refreshes always fetch fresh data.
        // Still deduplicates in-flight requests, so rapid SignalR messages won't cause parallel refreshes
        requestThrottleManager.executeBypassCooldown(requestKey, async () => {
            refreshRequestsContaining([apiEndpoints['getNoonReportsForShip'].url]);
            return true;
        });
    };

    const scheduleReconciliationRefresh = () => {
        if (reconciliationTimerRef.current) {
            clearTimeout(reconciliationTimerRef.current);
        }
        reconciliationTimerRef.current = setTimeout(() => {
            reconciliationTimerRef.current = null;
            reconcileGroupStatuses();
        }, 2000);
    };

    const eventMessageHandler = (message: SignalRResponse, scope?: EventApplicationScope): void => {
        if (!isCurrentEventApplicationScope(scope)) {
            return;
        }
        const text = JSON.parse(message.responseObject);
        const dto = text as TankersEventDataBM;
        if (!isCurrentEventApplicationScope(scope, dto)) {
            return;
        }
        if (dto.events && dto.events.length > 0) {
            refreshRequestsContaining(
                [apiEndpoints['getTankersEventDataDeltaForVoyage'].url],
                'voyageNumber1=' + dto.events[0].voyageNumber + '&imoNumber=' + dto.imoNumber
            );
            refreshRequestsContaining([apiEndpoints['getTankersEventDataDeltaForVoyageList'].url]);
            // A durable transaction remains pending until an event consumer commits it.
            // Do not make that optional consumer acknowledgement block the one global
            // SignalR queue, or unrelated AOL/Bunker messages cannot be handled.
            void serializeEventApplication(scope, async () => applyEventChanges(dto, scope, message.epoch))
                .then(() => {
                    if (isCurrentEventApplicationScope(scope, dto)) {
                        toastSuccess(message.sender + ' has updated events');
                    }
                })
                .catch(() => {
                    if (isCurrentEventApplicationScope(scope, dto)) {
                        logSignalRDiagnostic('handler_failure', {
                            vesselImoNumber: scope.vesselImoNumber,
                            messageType: message.type,
                        });
                    }
                });
        }
    };

    const aolSectionMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const dto = text as AolSectionSignalRDTO;

        throttledRefreshNoonReports(dto.imoNumber);

        updateAolList(dto);
        toggleToast(+dto.noonReportId, message, hubConnection);
    };

    const syncNoonReportFuelRobMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const dto = text as AolSectionSignalRDTO;

        throttledRefreshNoonReports(dto.imoNumber);

        if (locationPathname === '/aol') {
            setHasSyncError(false);

            const fromDate = formatAolUrlDate(aolFilter.fromDate);
            const toDate = formatAolUrlDate(aolFilter.toDate);
            const aolItemsPromise = getAolItems(dto.imoNumber, fromDate, toDate, true);
            const noonReportsPromise = getNoonReports(dto.imoNumber, fromDate, toDate);
            const loadedMasterStowagePlanPromise = getMasterStowagePlanFromApi(selectedVessel);

            Promise.all([aolItemsPromise, noonReportsPromise, loadedMasterStowagePlanPromise]).then(async (values) => {
                setAolItems([...values[0]]);
                const _aolDataItems = await makeAolDataItemList(values[0], values[1], isFeatureEnabled, selectedVessel, values[2]?.data);
                const _aolGroups = makeAolGroupList(_aolDataItems, selectedVessel);
                updateAolList(dto, { aolDataItems: _aolDataItems, aolGroups: _aolGroups });
                toggleToast(+dto.noonReportId, message, hubConnection);
            });
        }
    };

    const aolSectionUpdateMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const sectionSignalRUpdate = text as AolSectionSignalRUpdate;
        getUpdatedSection(sectionSignalRUpdate, message);

        throttledRefreshNoonReports(sectionSignalRUpdate.imoNumber);
    };

    const aolSectionDataUpdateMessageToOtherUsersHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const dto = text as AolSectionDataUpdateDto;
        updateAolStatusToOtherUsers(dto, message.sender);

        throttledRefreshNoonReports(dto.imoNumber);

        // Schedule trailing reconciliation for terminal transitions
        if (dto.isAolApproved || dto.isAolCompleted) {
            scheduleReconciliationRefresh();
        }
    };

    const addAolMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const dto = text as AolAddedDTO;
        addNoonReport(validatedNoonReportFactory.create(dto.noonReport as ValidatedNoonReportBM));

        throttledRefreshNoonReports(dto.imoNumber);

        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Notifications, date: new Date() })) {
            updateAolMissingTasks(dto.imoNumber);
        }
    };

    const deleteAolMessageHandler = (message: SignalRResponse) => {
        const deletePayload = JSON.parse(message.responseObject);
        updateOnDelete(undefined, deletePayload.noonReportGuid);
        toastError('An AOL was deleted by another user.');

        throttledRefreshNoonReports(+message.imoNumber);

        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Notifications, date: new Date() })) {
            updateAolMissingTasks(+message.imoNumber);
        }
    };

    const syncNoonReportPortLogHoursMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const noonReportBM = text as ValidatedNoonReportBM;
        const noonReport = validatedNoonReportFactory.create(noonReportBM);
        updateOnHoursSync(noonReport);
        toggleToast(noonReport.metadata.noonReportId, message, hubConnection);

        throttledRefreshNoonReports(noonReport.metadata.imoNumber);
    };

    const syncNoonReportImosPortCallIdMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const dto = text as ImosPortCallSyncDto;
        updateOnImosPortCallIdSync(0, +dto.imosPortCallId, dto.noonReportGuid);
        toggleToast(0, message, hubConnection);

        throttledRefreshNoonReports(+message.imoNumber);
    };

    const saveCargoTemperatureConfigsMessageHandler = (message: SignalRResponse) => {
        if (cargoTempLogDates != null && cargoTempLogDates.length > 0) {
            const cargoNumbers = getDistinctCargoNumbers(cargoTempLogDates);
            if (cargoNumbers && cargoNumbers.length > 0) {
                const cargoNumbersStr = getStringValue(cargoNumbers, ',');
                getCargoTemperatureConfigs(selectedVessel?.imoNumber, cargoNumbersStr, true);
                getCargoTemperatureInputFields(selectedVessel?.imoNumber, true);
                toastSuccess(message?.sender + ' has updated cargo temp. configuration');
            }
        }
    };

    const saveCargoTemperatureLogsMessageHandler = (_message: SignalRResponse) => {
        // Refresh cargo temp log dates for other users when someone saves a log
        refreshRequestsContaining([apiEndpoints['getCargoTempLogDates'].url]);
    };

    const deleteCargoTemperatureConfigsMessageHandler = (_message: SignalRResponse) => {
        if (cargoTempLogDates != null && cargoTempLogDates.length > 0) {
            const cargoNumbers = getDistinctCargoNumbers(cargoTempLogDates);
            if (cargoNumbers && cargoNumbers.length > 0) {
                const cargoNumbersStr = getStringValue(cargoNumbers, ',');
                getCargoTemperatureConfigs(selectedVessel?.imoNumber, cargoNumbersStr, true);
            }
        }
    };

    const upsertBunkeringMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const bunkeringBM = text as BunkeringBM;
        handleUpdatedBunkering(bunkeringBM);
        toggleBunkerToast(message, hubConnection, 'bunkering data');
        refreshRequestsContaining([apiEndpoints['getBunkerings'].url]);
    };

    const upsertBunkeringStatusMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const bunkeringStatusBM = text as BunkeringStatusBM;
        handleUpdatedBunkeringStatus(bunkeringStatusBM);
        toggleBunkerToast(message, hubConnection, 'a bunkering status');
        refreshRequestsContaining([apiEndpoints['getBunkerings'].url]);
    };

    const deleteBunkeringStatusMessageHandler = (message: SignalRResponse) => {
        const bunkeringStatusGuid = message.responseObject as string;
        handleDeletedBunkeringStatus(bunkeringStatusGuid);
        toggleBunkerToast(message, hubConnection, 'a bunkering status');
        refreshRequestsContaining([apiEndpoints['getBunkerings'].url]);
    };

    const upsertRobsMessageHandler = async (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const fuelRobData = text as FuelRobBM[];
        const offlineFuelRobs = await getAllFuelRobItems();

        handleUpdatedRobs(fuelRobData, offlineFuelRobs);
        toggleBunkerToast(message, hubConnection, 'ROB data');
        refreshRequestsContaining([apiEndpoints['getFuelRobData'].url]);
    };

    const upsertBunkerLossMessageHandler = (message: SignalRResponse) => {
        toggleBunkerToast(message, hubConnection, 'bunker loss data');
        refreshRequestsContaining([apiEndpoints['getBunkerLossData'].url]);
    };

    const imosUpdatesHandler = (_message: SignalRResponse) => {
        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Stowage, date: new Date(), imoNumber: selectedVessel?.imoNumber })) {
            toastSuccess('IMOS data has been updated...');

            if (!activeStowagePlan) {
                return;
            }

            refreshRequestsContaining([apiEndpoints['getRotation'].url], null, {
                productList,
                portNameList,
                berthNameList,
                selectedVessel,
                stowagePlanGuid: activeStowagePlan.stowagePlanGuid,
                stowagePlanTransitOperations: activeStowagePlan.transitOperations,
                stowagePlanRotationPortCallList,
                stowagePlanRotationCargoList,
            });
        }
    };

    const stowagePlanUpdatedHandler = (message: SignalRResponse) => {
        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Stowage, date: new Date(), imoNumber: selectedVessel?.imoNumber })) {
            const text = JSON.parse(message.responseObject);
            const updatedStowagePlanBM = text as StowagePlanBM;

            if (
                updatedStowagePlanBM.type === StowagePlanTypeEnum.Master &&
                isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber: selectedVessel.imoNumber, date: new Date() })
            ) {
                // Handle master stowage plan change
                updateLoadedMasterStowagePlan(updatedStowagePlanBM, message?.sender);
                refreshRequestsContaining([apiEndpoints['getMasterStowagePlan'].url]);
            }

            if (locationPathname === '/stowage') {
                onStowagePlanUpdated(updatedStowagePlanBM, message?.sender);
                refreshRequestsContaining([apiEndpoints['getStowagePlans'].url]);
                refreshRequestsContaining(
                    [apiEndpoints['getStowagePlanChanges'].url],
                    buildQueryParams({ stowagePlanGuid: updatedStowagePlanBM.stowagePlanGuid.toString() })
                );
            } else {
                removeRequestsContaining([apiEndpoints['getStowagePlans'].url]);
                removeRequestsContaining(
                    [apiEndpoints['getStowagePlanChanges'].url],
                    buildQueryParams({ stowagePlanGuid: updatedStowagePlanBM.stowagePlanGuid.toString() })
                );
            }
        }
    };

    const stowagePlanAddedHandler = (message: SignalRResponse) => {
        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Stowage, date: new Date(), imoNumber: selectedVessel?.imoNumber })) {
            const text = JSON.parse(message.responseObject);
            const updatedStowagePlanBM = text as StowagePlanBM;

            onStowagePlanAdded(updatedStowagePlanBM, message?.sender);
            refreshRequestsContaining([apiEndpoints['getStowagePlans'].url]);
        }
    };

    type MessageHandler = (message: SignalRResponse, scope?: EventApplicationScope) => void | Promise<void>;

    const handleSignalRResponse = async (
        responseType: SignalRMessageType,
        signalRResponse: SignalRResponse,
        hubConnectionForResponse: HubConnection,
        scope?: EventApplicationScope
    ) => {
        const responseHandlers: { [key in SignalRMessageType]?: { otherUsersHandler: MessageHandler; senderHandler: MessageHandler | null } } = {
            event: { otherUsersHandler: eventMessageHandler, senderHandler: null },
            aolSection: { otherUsersHandler: aolSectionMessageHandler, senderHandler: null },
            syncNoonReportFuelRob: { otherUsersHandler: syncNoonReportFuelRobMessageHandler, senderHandler: null },
            aolSectionUpdate: { otherUsersHandler: aolSectionUpdateMessageHandler, senderHandler: null },
            aolSectionDataUpdate: {
                otherUsersHandler: aolSectionDataUpdateMessageToOtherUsersHandler,
                senderHandler: null,
            },
            addAol: { otherUsersHandler: addAolMessageHandler, senderHandler: null },
            deleteAol: { otherUsersHandler: deleteAolMessageHandler, senderHandler: null },
            syncNoonReportPortLogHours: { otherUsersHandler: syncNoonReportPortLogHoursMessageHandler, senderHandler: null },
            syncNoonReportImosPortCallId: { otherUsersHandler: syncNoonReportImosPortCallIdMessageHandler, senderHandler: null },
            saveCargoTemperatureConfigs: { otherUsersHandler: saveCargoTemperatureConfigsMessageHandler, senderHandler: null },
            upsertBunkering: { otherUsersHandler: upsertBunkeringMessageHandler, senderHandler: null },
            upsertBunkeringStatus: { otherUsersHandler: upsertBunkeringStatusMessageHandler, senderHandler: null },
            deleteBunkeringStatus: { otherUsersHandler: deleteBunkeringStatusMessageHandler, senderHandler: null },
            upsertRobs: { otherUsersHandler: upsertRobsMessageHandler, senderHandler: null },
            upsertBunkerLoss: { otherUsersHandler: upsertBunkerLossMessageHandler, senderHandler: null },
            imosUpdates: { otherUsersHandler: imosUpdatesHandler, senderHandler: null },
            stowagePlanUpdated: { otherUsersHandler: stowagePlanUpdatedHandler, senderHandler: null },
            stowagePlanAdded: { otherUsersHandler: stowagePlanAddedHandler, senderHandler: null },
            deleteCargoTemperatureConfigs: { otherUsersHandler: deleteCargoTemperatureConfigsMessageHandler, senderHandler: null },
            saveCargoTemperatureLogs: { otherUsersHandler: saveCargoTemperatureLogsMessageHandler, senderHandler: null },
        };

        const handlers = responseHandlers[responseType];
        if (handlers) {
            if (signalRResponse.connectionId !== hubConnectionForResponse.connectionId) {
                await handlers.otherUsersHandler?.(signalRResponse, scope);
            } else {
                await handlers.senderHandler?.(signalRResponse, scope);
            }
        }
    };

    return { handleSignalRResponse };
};
